import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import moment from 'moment';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import SportsRoundedIcon from '@mui/icons-material/SportsRounded';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import FeedOutlinedIcon from '@mui/icons-material/FeedOutlined';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import { School } from 'models/school';
import { SchoolEvent } from 'models/event';
import { SchoolHouse } from 'models/house';
import { Photo } from 'models/photo';
import { SchoolNews } from 'models/news';
import { HouseTotalsRow, getSchoolHousePointsTotalsCurrentYear } from 'services/awards';
import { getNews } from 'services/news';
import { getSchoolEvents, getSchoolHouses, getSchoolPhotos } from 'services/school';
import { getFilterForSchoolFixturesForHomePage, getFilterForSchoolResultsForHomePage, getFilterForSchoolResultsWithoutClubsAndTournaments } from 'helpers/filters';
import { getEventGeneratedName, isEventStatusCanceled } from 'helpers/event';
import { getEventResultForCalendar } from 'helpers/calendar';
import { getAgesGroupFormatted } from 'helpers/accessors';
import { getVenueView } from 'helpers/venue';
import { resolveSchoolPublicSite } from 'helpers/publicSite';
import Loader from 'components/loader';
import HomeSlider from 'components/HomeSlider';
import './Home.scss';

interface Props {
   school: School;
}

interface HomeData {
   houses: SchoolHouse[];
   photos: Photo[];
   fixtures: SchoolEvent[];
   results: SchoolEvent[];
   seasonResults: SchoolEvent[];
   houseTotals: HouseTotalsRow[];
   news: SchoolNews[];
}

const EMPTY_HOME_DATA: HomeData = {
   houses: [],
   photos: [],
   fixtures: [],
   results: [],
   seasonResults: [],
   houseTotals: [],
   news: [],
};

function safeRequest<T>(request: Promise<T>, fallback: T): Promise<T> {
   return request.catch(() => fallback);
}

function getEventTitle(event: SchoolEvent, schoolId: string) {
   return getEventGeneratedName(event, schoolId)
      || event.sport?.name
      || 'School event';
}

function getEventVenue(event: SchoolEvent, schoolId: string) {
   const venue = getVenueView(event, schoolId);
   const postcode = event.venue?.postcodeData?.postcode;
   return [venue, postcode].filter(Boolean).join(', ') || 'Venue to be confirmed';
}

function getEventTypeLabel(event: SchoolEvent) {
   if (event.clubId) return 'Club event';
   if (event.tournamentId) return 'Tournament';
   return event.sport?.name || 'School sport';
}

function getEventOutcome(event: SchoolEvent, schoolId: string) {
   const schoolScores = event.results?.schoolScore || [];
   const ownSchoolScore = schoolScores.find(score => score.schoolId === schoolId);
   if (ownSchoolScore?.isWinner) return 'W';
   if (ownSchoolScore && schoolScores.some(score => score.schoolId !== schoolId && score.isWinner)) return 'L';
   if (ownSchoolScore && schoolScores.length > 1 && !schoolScores.some(score => score.isWinner)) return 'D';

   const ownTeamIds = new Set((event.teamsData || []).filter(team => team.schoolId === schoolId).map(team => team.id));
   const teamScores = event.results?.teamScore || [];
   if (teamScores.some(score => ownTeamIds.has(score.teamId) && score.isWinner)) return 'W';
   if (teamScores.some(score => !ownTeamIds.has(score.teamId) && score.isWinner)) return 'L';
   return '';
}

function getCurrentSeasonStart() {
   const today = new Date();
   return new Date(today.getMonth() >= 8 ? today.getFullYear() : today.getFullYear() - 1, 8, 1);
}

function escapeCalendarValue(value: string) {
   return value.replace(/([,;])/g, '\\$1').replace(/\r?\n/g, '\\n');
}

function toCalendarDate(value?: string) {
   return value ? moment(value).utc().format('YYYYMMDD[T]HHmmss[Z]') : '';
}

function downloadEventCalendar(event: SchoolEvent, school: School) {
   const title = getEventTitle(event, school.id);
   const location = getEventVenue(event, school.id);
   const endTime = event.endTime || moment(event.startTime).add(1, 'hour').toISOString();
   const content = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Squad In Touch//School sport//EN',
      'BEGIN:VEVENT',
      `UID:${event.id}@squadintouch.com`,
      `DTSTAMP:${moment().utc().format('YYYYMMDD[T]HHmmss[Z]')}`,
      `DTSTART:${toCalendarDate(event.startTime)}`,
      `DTEND:${toCalendarDate(endTime)}`,
      `SUMMARY:${escapeCalendarValue(title)}`,
      `LOCATION:${escapeCalendarValue(location)}`,
      `DESCRIPTION:${escapeCalendarValue(`${school.name} sport event`)}`,
      'END:VEVENT',
      'END:VCALENDAR',
   ].join('\r\n');
   const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' });
   const url = URL.createObjectURL(blob);
   const anchor = document.createElement('a');
   anchor.href = url;
   anchor.download = `${title.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase() || 'school-event'}.ics`;
   anchor.click();
   URL.revokeObjectURL(url);
}

function EventRow({ event, school, isResult = false }: { event: SchoolEvent; school: School; isResult?: boolean }) {
   const title = getEventTitle(event, school.id);
   const result = isResult ? getEventResultForCalendar(event, school.id) : '';
   const venue = getVenueView(event, school.id);
   const isCanceled = isEventStatusCanceled(event);
   const outcome = isResult ? getEventOutcome(event, school.id) : '';
   const ages = event.ages?.length ? getAgesGroupFormatted({ ages: [...event.ages] }, school) : '';

   return (
      <Link className='school-home-event' to={`/event/${event.id}`}>
         <time className='school-home-event__date' dateTime={event.startTime}>
            <strong>{moment(event.startTime).format('D MMM')}</strong>
            <span>{moment(event.startTime).format('HH:mm')}</span>
         </time>
         {outcome && <span className={`school-home-event__outcome is-${outcome.toLowerCase()}`}>{outcome}</span>}
         <div className='school-home-event__content'>
            <div className='school-home-event__badges'>
               <span>{event.sport?.name || 'Sport'}</span>
               {venue && <span className={`school-home-event__venue is-${venue.toLowerCase()}`}>{venue}</span>}
               {isCanceled && <span className='school-home-event__cancelled'>Cancelled</span>}
            </div>
            <strong className='school-home-event__title'>{title}</strong>
            <span className='school-home-event__details'>
               {isResult && <b>{result || 'View results'}</b>}
               {ages && <span>{ages}</span>}
               <span>{getEventVenue(event, school.id)}</span>
            </span>
         </div>
         <ArrowForwardRoundedIcon className='school-home-event__arrow' aria-hidden='true' />
      </Link>
   );
}

function EmptySection({ icon, title, text }: { icon: React.ReactElement; title: string; text: string }) {
   return (
      <div className='school-home-empty'>
         <span className='school-home-empty__icon' aria-hidden='true'>{icon}</span>
         <div>
            <strong>{title}</strong>
            <p>{text}</p>
         </div>
      </div>
   );
}

export default function Home({ school }: Props) {
   const [isLoading, setIsLoading] = useState(true);
   const [data, setData] = useState<HomeData>(EMPTY_HOME_DATA);
   const navigate = useNavigate();
   const settings = resolveSchoolPublicSite(school);
   const defaultAlbumId = school.defaultAlbumId || '';

   const loadHome = useCallback(async () => {
      setIsLoading(true);

      const [houses, fixtures, results, seasonResults, houseTotals, photos, news] = await Promise.all([
         settings.pages.housePoints ? safeRequest(getSchoolHouses(school), []) : Promise.resolve([]),
         settings.pages.fixturesAndResults
            ? safeRequest(getSchoolEvents(school.id, getFilterForSchoolFixturesForHomePage(school.id)), [])
            : Promise.resolve([]),
         settings.pages.fixturesAndResults
            ? safeRequest(getSchoolEvents(school.id, getFilterForSchoolResultsForHomePage()), [])
            : Promise.resolve([]),
         settings.pages.fixturesAndResults
            ? safeRequest(getSchoolEvents(school.id, {
               ...getFilterForSchoolResultsWithoutClubsAndTournaments({
                  startTime: { $gte: getCurrentSeasonStart().toISOString(), $lte: new Date().toISOString() }
               }),
               limit: 1000
            }), [])
            : Promise.resolve([]),
         settings.pages.housePoints
            ? safeRequest(getSchoolHousePointsTotalsCurrentYear(school.id), [])
            : Promise.resolve([]),
         defaultAlbumId
            ? safeRequest(getSchoolPhotos(school, defaultAlbumId), [])
            : Promise.resolve([]),
         settings.pages.news
            ? safeRequest(getNews(school.id, { where: {}, skip: 0, limit: 2, order: 'date DESC' }), [])
            : Promise.resolve([]),
      ]);

      setData({
         houses,
         fixtures,
         results,
         seasonResults,
         houseTotals: [...houseTotals].sort((left, right) => right.totalPoints - left.totalPoints),
         photos,
         news,
      });
      setIsLoading(false);
   }, [defaultAlbumId, school, settings.pages.fixturesAndResults, settings.pages.housePoints, settings.pages.news]);

   useEffect(() => {
      loadHome();
   }, [loadHome]);

   const publicPhotos = useMemo(
      () => data.photos.filter(photo => photo.accessPreset === 'PUBLIC' && photo.picUrl),
      [data.photos]
   );
   const heroPhotos = publicPhotos.slice(0, 12);
   // The homepage feature must represent school sport. Club events have their
   // own public section and must never displace the next school fixture here.
   const nextEvent = data.fixtures.find(event => !event.clubId);
   const totalsByHouse = new Map(data.houseTotals.map(total => [total.houseId, total]));
   const housesWithTotals = data.houses
      .map(house => ({ house, total: totalsByHouse.get(house.id)?.totalPoints || 0 }))
      .sort((left, right) => right.total - left.total);
   const maxHouseTotal = Math.max(...housesWithTotals.map(item => item.total), 1);
   const seasonStats = data.seasonResults.reduce((stats, event) => {
      const outcome = getEventOutcome(event, school.id);
      if (outcome === 'W') stats.won += 1;
      if (outcome === 'D') stats.drawn += 1;
      if (outcome === 'L') stats.lost += 1;
      return stats;
   }, { won: 0, drawn: 0, lost: 0 });

   if (isLoading) {
      return <div className='school-home-loading'><Loader /></div>;
   }

   return (
      <div className='school-home'>
         {heroPhotos.length > 0 && (
            <section className='school-home-gallery-hero' aria-label={`${school.name} photo gallery`}>
               <HomeSlider
                  imageUrls={heroPhotos.map(photo => photo.picUrl)}
                  captions={heroPhotos.map(photo => photo.description?.trim() || '')}
               />
            </section>
         )}

         {nextEvent ? (
            <section className='school-home-next'>
               <>
                  <div className='school-home-next__content'>
                     <div className='school-home-next__eyebrow'>
                        <span>Next up</span>
                        <span className='school-home-next__sport'>{getEventTypeLabel(nextEvent)}</span>
                     </div>
                     <h1>{getEventTitle(nextEvent, school.id)}</h1>
                     <div className='school-home-next__details'>
                        <span><CalendarMonthRoundedIcon />{moment(nextEvent.startTime).format('ddd D MMM YYYY')}</span>
                        <span><ScheduleRoundedIcon />{moment(nextEvent.startTime).format('h:mm a')}</span>
                        <span><LocationOnOutlinedIcon />{getEventVenue(nextEvent, school.id)}</span>
                     </div>
                  </div>
                  <div className='school-home-next__actions'>
                     <span>{moment(nextEvent.startTime).fromNow()}</span>
                     <button type='button' className='school-home-button is-light' onClick={() => downloadEventCalendar(nextEvent, school)}>
                        <CalendarMonthRoundedIcon /> Add to calendar
                     </button>
                     <button type='button' className='school-home-button is-outline' onClick={() => navigate(`/event/${nextEvent.id}`)}>
                        Fixture details
                     </button>
                  </div>
               </>
            </section>
         ) : heroPhotos.length === 0 ? (
            <section className='school-home-next is-school-fallback'>
               <div className='school-home-next__empty'>
                  <span className='school-home-next__eyebrow'>Sport at {school.name}</span>
                  <h1>{school.name}</h1>
                  <p>{school.description || 'Fixtures, results and school sport updates will appear here as soon as the school publishes them.'}</p>
                  {settings.pages.calendar && <Link className='school-home-button is-light' to='/calendar'>View school calendar</Link>}
               </div>
               <div className='school-home-next__crest' aria-hidden='true'>
                  {school.pic ? <img src={school.pic} alt='' /> : <SportsRoundedIcon />}
               </div>
            </section>
         ) : null}

         <div className='school-home-grid'>
            <div className='school-home-grid__main'>
               {settings.pages.fixturesAndResults && (
                  <section className='school-home-section'>
                     <header className='school-home-section__header'>
                        <h2>Upcoming fixtures</h2>
                        {data.fixtures.length > 0 && <Link to='/fixtures'>All fixtures <ArrowForwardRoundedIcon /></Link>}
                     </header>
                     {data.fixtures.length > 0 ? (
                        <div className='school-home-event-list'>
                           {data.fixtures.slice(0, 4).map(event => <EventRow key={event.id} event={event} school={school} />)}
                        </div>
                     ) : (
                        <EmptySection
                           icon={<CalendarMonthRoundedIcon />}
                           title='Nothing scheduled yet'
                           text='Published fixtures and activities will appear here automatically.'
                        />
                     )}
                  </section>
               )}

               {settings.pages.fixturesAndResults && (
                  <section className='school-home-section'>
                     <header className='school-home-section__header'>
                        <h2>Recent results</h2>
                        {data.results.length > 0 && <Link to='/results'>All results <ArrowForwardRoundedIcon /></Link>}
                     </header>
                     {data.results.length > 0 ? (
                        <div className='school-home-event-list'>
                           {data.results.slice(0, 4).map(event => <EventRow key={event.id} event={event} school={school} isResult />)}
                        </div>
                     ) : (
                        <EmptySection
                           icon={<SportsRoundedIcon />}
                           title='No results published yet'
                           text={data.fixtures.length > 0
                              ? 'Results will appear here after the first completed events.'
                              : 'Results will appear once the school has published and completed its first events.'}
                        />
                     )}
                  </section>
               )}

               {(school.description || school.pic) && (
                  <section className='school-home-about'>
                     {school.pic && <img src={school.pic} alt={`${school.name} crest`} />}
                     <div>
                        <span>About our sport</span>
                        <h2>{school.name}</h2>
                        {school.description && <p>{school.description}</p>}
                     </div>
                  </section>
               )}

               {settings.pages.news && (
                  <section className='school-home-section'>
                     <header className='school-home-section__header'>
                        <h2>Latest news</h2>
                        {data.news.length > 0 && <Link to='/news'>All news <ArrowForwardRoundedIcon /></Link>}
                     </header>
                     {data.news.length > 0 ? (
                        <div className='school-home-news'>
                           {data.news.map(item => (
                              <Link key={item.id} className='school-home-news__card' to={`/news?article=${item.id}`}>
                                 {item.picUrl && <img src={item.picUrl} alt='' />}
                                 <div>
                                    <time dateTime={item.date}>{moment(item.date).format('D MMM YYYY')}</time>
                                    <h3>{item.title}</h3>
                                    <span>Read story <ArrowForwardRoundedIcon /></span>
                                 </div>
                              </Link>
                           ))}
                        </div>
                     ) : (
                        <EmptySection
                           icon={<FeedOutlinedIcon />}
                           title='No news published yet'
                           text='School sport updates will appear here when they are ready to share.'
                        />
                     )}
                  </section>
               )}
            </div>

            <aside className='school-home-grid__aside'>
               {settings.pages.fixturesAndResults && (
                  <section className='school-home-panel school-home-season'>
                     <div className='school-home-panel__heading'>
                        <span className='school-home-panel__icon'><BarChartRoundedIcon /></span>
                        <div>
                           <h2>This season</h2>
                           <p>Since 1 September</p>
                        </div>
                     </div>
                     <dl className='school-home-season__stats'>
                        <div className='is-played'><dt>Played</dt><dd>{data.seasonResults.length}</dd></div>
                        <div className='is-won'><dt>Won</dt><dd>{seasonStats.won}</dd></div>
                        <div className='is-drawn'><dt>Drawn</dt><dd>{seasonStats.drawn}</dd></div>
                        <div className='is-lost'><dt>Lost</dt><dd>{seasonStats.lost}</dd></div>
                     </dl>
                     <Link className='school-home-season__link' to='/results'>View all results <ArrowForwardRoundedIcon /></Link>
                  </section>
               )}

               {settings.pages.housePoints && data.houses.length > 0 && (
                  <section className='school-home-panel' id='house-points'>
                     <div className='school-home-panel__heading'>
                        <span className='school-home-panel__icon'><EmojiEventsOutlinedIcon /></span>
                        <div>
                           <h2>House points</h2>
                           <p>Current academic year</p>
                        </div>
                     </div>
                     {data.houseTotals.length > 0 ? (
                        <ol className='school-home-house-list'>
                           {housesWithTotals.map(({ house, total }, index) => (
                              <li key={house.id}>
                                 <span className='school-home-house-list__position'>{index + 1}</span>
                                 <span className='school-home-house-list__content'>
                                    <span><strong>{house.name}</strong><b>{total.toLocaleString()}</b></span>
                                    <i><span style={{ width: `${Math.max(4, total / maxHouseTotal * 100)}%`, background: house.colors?.[0] || 'var(--sc-brand)' }} /></i>
                                 </span>
                              </li>
                           ))}
                        </ol>
                     ) : (
                        <p className='school-home-panel__empty'>No points have been awarded this season yet. The table will appear after the first house event is scored.</p>
                     )}
                  </section>
               )}

               <section className='school-home-panel is-subscribe'>
                  <span className='school-home-panel__icon'><CalendarMonthRoundedIcon /></span>
                  <h2>Never miss a fixture</h2>
                  <p>Subscribe once and published school fixtures will update in your calendar automatically.</p>
                  <Link className='school-home-button is-brand' to='/subscribe'>Subscription options</Link>
               </section>

               {settings.pages.photos && (
                  <section className='school-home-panel'>
                     <div className='school-home-panel__heading'>
                        <span className='school-home-panel__icon'><PhotoCameraOutlinedIcon /></span>
                        <div>
                           <h2>Photos</h2>
                           <p>Published school albums</p>
                        </div>
                     </div>
                     {publicPhotos.length > 0 ? (
                        <div className='school-home-photo-grid'>
                           {publicPhotos.slice(0, 4).map(photo => <img key={photo.id} src={photo.picUrl} alt='' loading='lazy' />)}
                        </div>
                     ) : (
                        <p className='school-home-panel__empty'>No public photos have been added yet.</p>
                     )}
                  </section>
               )}
            </aside>
         </div>
      </div>
   );
}
